import type { Db } from '../clients.ts';
import { scoped, type TenantScope } from '../tenant.ts';

/**
 * Stock movements.
 *
 * Quantities move ONLY through `public.inventory_move`, which is granted to
 * `service_role` alone: it writes the `stock_movements` ledger and the
 * `inventory_levels` total in one place, so the running total can never drift
 * from the entries that explain it. `authenticated` is granted UPDATE on
 * exactly one column of `inventory_levels` -- `low_stock_threshold` -- which is
 * the schema stating the same rule.
 *
 * That is why this is the one catalog write that goes through the API rather
 * than browser -> PostgREST: it needs service-role, and by the rule in
 * docs/authorization.md that puts the API layer in charge of authorizing it.
 */

/**
 * Movement types staff may write by hand.
 *
 * `sold`, `allocated` and `released` are excluded because orders own them, and
 * the transfer types belong to the transfer flow. `direction` is the sign the
 * ledger entry must carry, which the caller validates against so a "received"
 * cannot quietly remove stock.
 */
export const ADJUST_REASONS = [
  {
    value: 'adjusted',
    label: 'Manual adjustment',
    direction: 'any',
    hint: 'Correcting the number on the shelf.',
  },
  {
    value: 'received',
    label: 'Received',
    direction: 'increase',
    hint: 'Stock arrived outside a purchase order.',
  },
  {
    value: 'returned',
    label: 'Customer return',
    direction: 'increase',
    hint: 'Returned to sellable stock.',
  },
  {
    value: 'destroyed',
    label: 'Destroyed or wasted',
    direction: 'decrease',
    hint: 'Damaged, expired or otherwise unsellable.',
  },
  {
    value: 'audit_correction',
    label: 'Audit correction',
    direction: 'any',
    hint: 'Reconciling against a counted audit.',
  },
] as const;

export type AdjustReason = (typeof ADJUST_REASONS)[number]['value'];

export const ADJUST_REASON_VALUES = ADJUST_REASONS.map((reason) => reason.value) as [
  AdjustReason,
  ...AdjustReason[],
];

export function adjustReason(value: string) {
  return ADJUST_REASONS.find((reason) => reason.value === value) ?? ADJUST_REASONS[0];
}

/** Returns a message when `delta` contradicts the reason, else null. */
export function checkAdjustDirection(reason: string, delta: number): string | null {
  const meta = adjustReason(reason);

  if (meta.direction === 'increase' && delta < 0) {
    return `"${meta.label}" adds stock. Use a negative amount with a different reason.`;
  }

  if (meta.direction === 'decrease' && delta > 0) {
    return `"${meta.label}" removes stock. Enter a negative amount.`;
  }

  if (delta === 0) return 'Enter an amount to move.';

  return null;
}

export interface InventoryLevel {
  id: number;
  variantId: string;
  locationId: string;
  roomId: string | null;
  onHand: number;
  reserved: number;
  available: number;
  lowStockThreshold: number | null;
}

export async function getInventoryLevel(
  db: Db,
  scope: TenantScope,
  levelId: number,
): Promise<InventoryLevel | null> {
  const { data, error } = await scoped(
    db
      .from('inventory_levels')
      .select(
        'id, variant_id, location_id, room_id, on_hand, reserved, available, low_stock_threshold',
      ),
    scope,
  )
    .eq('id', levelId)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  return {
    id: data.id,
    variantId: data.variant_id,
    locationId: data.location_id,
    roomId: data.room_id,
    onHand: Number(data.on_hand ?? 0),
    reserved: Number(data.reserved ?? 0),
    available: Number(data.available ?? 0),
    lowStockThreshold: data.low_stock_threshold === null ? null : Number(data.low_stock_threshold),
  };
}

export class InventoryMoveError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InventoryMoveError';
  }
}

/**
 * Apply a manual stock movement.
 *
 * The location and room are read from the level rather than taken from the
 * caller: the client knows which *record* is being adjusted, and letting it
 * also name where the stock sits would let a movement be booked against a
 * location the level does not belong to.
 */
export async function adjustStock(
  db: Db,
  scope: TenantScope,
  input: {
    level: InventoryLevel;
    quantityDelta: number;
    reason: AdjustReason;
    note: string;
    actorId: string;
    actorName: string;
  },
): Promise<void> {
  const meta = adjustReason(input.reason);

  const { error } = await db.rpc('inventory_move', {
    p_account_id: scope.accountId,
    p_variant_id: input.level.variantId,
    p_location_id: input.level.locationId,
    // Nullable in SQL; the generated type marks it required.
    p_room_id: input.level.roomId as string,
    p_type: input.reason,
    p_quantity: input.quantityDelta,
    p_reason: input.note || meta.label,
    p_actor_id: input.actorId,
    p_actor_name: input.actorName,
  });

  if (error) {
    // The function raises a readable message for the cases staff can cause
    // (going below zero, unknown variant); anything else is ours to fix.
    if (/negative|insufficient|not found|exceeds/i.test(error.message)) {
      throw new InventoryMoveError(error.message);
    }

    throw error;
  }
}

/**
 * The low-stock alert level.
 *
 * Separate from the movement on purpose: this one is a plain column the RLS
 * client is granted, so it does not need service-role and must not be bundled
 * into a call that does.
 */
export async function setLowStockThreshold(
  db: Db,
  scope: TenantScope,
  levelId: number,
  threshold: number | null,
): Promise<void> {
  const { error } = await scoped(
    db.from('inventory_levels').update({ low_stock_threshold: threshold }),
    scope,
  ).eq('id', levelId);

  if (error) throw error;
}
